import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { getLatestVersion } from '../../Objects/Version';

interface Props {
  children: ReactNode;
  userEmail: string;
  userRole: 'viewer' | 'admin';
  onSignOut: () => void;
}

type IconName = 'overview' | 'sales' | 'food' | 'labor' | 'cost' | 'loan' | 'upload' | 'target' | 'store' | 'integration' | 'trend' | 'calendar';
type MenuGroupId = 'analysis' | 'kpi' | 'operations' | 'settings' | 'admin';
type OpenGroups = Record<MenuGroupId, boolean>;
const defaultOpenGroups: OpenGroups = { analysis: true, kpi: true, operations: true, settings: true, admin: true };
export const sidebarSessionKey = (email: string): string => `loop-dashboard-sidebar:${email.toLowerCase()}`;

const savedOpenGroups = (email: string): OpenGroups => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(sidebarSessionKey(email)) ?? '{}') as Record<string, unknown>;
    return {
      analysis: typeof stored.analysis === 'boolean' ? stored.analysis : true,
      kpi: typeof stored.kpi === 'boolean' ? stored.kpi : true,
      operations: typeof stored.operations === 'boolean' ? stored.operations : true,
      settings: typeof stored.settings === 'boolean' ? stored.settings : true,
      admin: typeof stored.admin === 'boolean' ? stored.admin : true,
    };
  } catch {
    return { ...defaultOpenGroups };
  }
};

const pageTitles: Record<string, { eyebrow: string; title: string }> = {
  '/': { eyebrow: 'MANAGEMENT OVERVIEW', title: '経営サマリー' },
  '/sales': { eyebrow: 'SALES INTELLIGENCE', title: '売上分析' },
  '/food': { eyebrow: 'FOOD COST', title: '食材費' },
  '/labor': { eyebrow: 'LABOR MANAGEMENT', title: '人件費' },
  '/fixed-costs': { eyebrow: 'FIXED COST', title: '固定費' },
  '/loans': { eyebrow: 'FINANCE', title: '借入' },
  '/settings/upload': { eyebrow: 'DATA IMPORT', title: 'CSV取込' },
  '/settings/targets': { eyebrow: 'BUSINESS TARGET', title: '目標値' },
  '/settings/stores': { eyebrow: 'STORE DIRECTORY', title: '店舗マスタ' },
  '/sales/daily': { eyebrow: 'DAILY SALES FLASH', title: '売上速報' },
  '/kpi/drinks': { eyebrow: 'KPI LABORATORY', title: 'ドリンク分析' },
  '/kpi/weekly-sales': { eyebrow: 'KPI LABORATORY', title: '週間売上' },
  '/kpi/year-over-year': { eyebrow: 'KPI LABORATORY', title: '昨年対比' },
  '/kpi/monthly-sales': { eyebrow: 'KPI LABORATORY', title: '月間売上' },
  '/kpi/annual-sales': { eyebrow: 'KPI LABORATORY', title: '年間売上' },
  '/kpi/yearly-sales': { eyebrow: 'KPI LABORATORY', title: '年別売上' },
  '/kpi/stay-occupancy': { eyebrow: 'KPI LABORATORY', title: '滞在時間と人数' },
  '/kpi/occupancy-heatmap': { eyebrow: 'KPI LABORATORY', title: '座席稼働率ヒートマップ' },
  '/kpi/profit-simulator': { eyebrow: 'KPI LABORATORY', title: '利益シミュレーター' },
  '/calendar/events': { eyebrow: 'OPERATIONS CALENDAR', title: 'イベント' },
  '/inventory': { eyebrow: 'OPERATIONS / INVENTORY', title: '在庫チェック' },
  '/documents': { eyebrow: 'DOCUMENT ARCHIVE', title: '証憑・資料' },
  '/account': { eyebrow: 'IDENTITY', title: 'アカウント' },
  '/version': { eyebrow: 'RELEASE NOTES', title: 'バージョン履歴' },
};

export const AppShell = ({ children, userEmail, userRole, onSignOut }: Props) => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<OpenGroups>(() => savedOpenGroups(userEmail));
  const isAdmin = userRole === 'admin';
  const currentPage = pageTitles[location.pathname] ?? pageTitles['/'];
  const toggleGroup = (id: MenuGroupId) => setOpenGroups((current) => ({ ...current, [id]: !current[id] }));

  useEffect(() => {
    sessionStorage.setItem(sidebarSessionKey(userEmail), JSON.stringify(openGroups));
  }, [openGroups, userEmail]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [drawerOpen]);

  return (
    <div className="app-shell">
      <aside className="app-sidebar" aria-label="メインメニュー">
        <SidebarContent isAdmin={isAdmin} userEmail={userEmail} onSignOut={onSignOut} openGroups={openGroups} onToggleGroup={toggleGroup} instanceId="desktop" />
      </aside>

      <header className="app-mobile-header">
        <Link to="/" className="app-mobile-brand" aria-label="トップへ">
          <BrandMark compact />
        </Link>
        <button
          type="button"
          className="app-menu-button"
          aria-label="メニューを開く"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
        >
          <span />
          <span />
        </button>
      </header>

      {drawerOpen && (
        <div className="app-drawer-layer">
          <button
            type="button"
            className="app-drawer-backdrop"
            aria-label="メニューを閉じる"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="app-drawer" aria-label="モバイルメニュー">
            <button
              type="button"
              className="app-drawer-close"
              aria-label="メニューを閉じる"
              onClick={() => setDrawerOpen(false)}
            >
              ×
            </button>
            <SidebarContent isAdmin={isAdmin} userEmail={userEmail} onSignOut={onSignOut} openGroups={openGroups} onToggleGroup={toggleGroup} instanceId="mobile" />
          </aside>
        </div>
      )}

      <div className="app-workspace">
        <header className="app-context-header">
          <div>
            <span className="app-context-eyebrow">{currentPage.eyebrow}</span>
            <strong className="app-context-title">{currentPage.title}</strong>
          </div>
          <div className="app-context-meta">
            <span>LOOP HAMBURG</span>
            <span>{new Intl.DateTimeFormat('ja-JP', { dateStyle: 'long' }).format(new Date())}</span>
          </div>
        </header>
        <main className="app-content">{children}</main>
      </div>
    </div>
  );
};

interface SidebarContentProps {
  isAdmin: boolean;
  userEmail: string;
  onSignOut: () => void;
  openGroups: OpenGroups;
  onToggleGroup: (id: MenuGroupId) => void;
  instanceId: 'desktop' | 'mobile';
}

const SidebarContent = ({ isAdmin, userEmail, onSignOut, openGroups, onToggleGroup, instanceId }: SidebarContentProps) => (
  <div className="sidebar-inner">
    <Link to="/" className="sidebar-brand" aria-label="Loop Hamburg 経営ダッシュボード">
      <BrandMark />
    </Link>

    <nav className="sidebar-nav">
      <NavGroup id="analysis" label="経営分析" open={openGroups.analysis} onToggle={onToggleGroup} instanceId={instanceId}>
        <SidebarLink to="/" label="経営サマリー" icon="overview" end />
        <SidebarLink to="/sales" label="売上分析" icon="sales" />
        <SidebarLink to="/sales/daily" label="売上速報" icon="integration" />
        <SidebarLink to="/food" label="食材費" icon="food" />
      </NavGroup>
      <NavGroup id="kpi" label="KPI" open={openGroups.kpi} onToggle={onToggleGroup} instanceId={instanceId}>
        <SidebarLink to="/kpi/yearly-sales" label="年別売上" icon="trend" />
        <SidebarLink to="/kpi/annual-sales" label="年間売上" icon="trend" />
        <SidebarLink to="/kpi/monthly-sales" label="月間売上" icon="trend" />
        <SidebarLink to="/kpi/weekly-sales" label="週間売上" icon="trend" />
        <SidebarLink to="/kpi/year-over-year" label="昨年対比" icon="trend" />
        <SidebarLink to="/kpi/drinks" label="ドリンク分析" icon="trend" />
        <SidebarLink to="/kpi/stay-occupancy" label="滞在時間と人数" icon="trend" />
        <SidebarLink to="/kpi/occupancy-heatmap" label="座席稼働率ヒートマップ" icon="trend" />
        <SidebarLink to="/kpi/profit-simulator" label="利益シミュレーター" icon="trend" />
      </NavGroup>
      <NavGroup id="operations" label="業務管理" open={openGroups.operations} onToggle={onToggleGroup} instanceId={instanceId}>
        <SidebarLink to="/inventory" label="在庫チェック" icon="store" />
      </NavGroup>
      <NavGroup id="settings" label="設定" open={openGroups.settings} onToggle={onToggleGroup} instanceId={instanceId}>
        <SidebarLink to="/calendar/events" label="イベント" icon="calendar" />
        <SidebarLink to="/settings/targets" label="目標値" icon="target" />
        <SidebarLink to="/fixed-costs" label="固定費" icon="cost" />
        <SidebarLink to="/settings/stores" label="店舗マスタ" icon="store" />
        <SidebarLink to="/settings/upload" label="CSV取り込み" icon="upload" />
      </NavGroup>
      {isAdmin && (
        <NavGroup id="admin" label="管理者専用" open={openGroups.admin} onToggle={onToggleGroup} instanceId={instanceId}>
          <SidebarLink to="/loans" label="借入" icon="loan" />
          <SidebarLink to="/labor" label="人件費" icon="labor" />
          <SidebarLink to="/documents" label="証憑・資料" icon="upload" />
        </NavGroup>
      )}
    </nav>

    <div className="sidebar-footer">
      <NavLink
        to="/account"
        aria-label={`アカウント: ${userEmail}`}
        className={({ isActive }) => `sidebar-user${isActive ? ' sidebar-user-active' : ''}`}
      >
        <span className="sidebar-user-label">SIGNED IN</span>
        <span className="sidebar-user-email" title={userEmail}>{userEmail}</span>
      </NavLink>
      <div className="sidebar-footer-actions">
        <Link to="/version" className="sidebar-version">{getLatestVersion()}</Link>
        <button type="button" className="sidebar-signout" onClick={onSignOut}>ログアウト</button>
      </div>
    </div>
  </div>
);

const BrandMark = ({ compact = false }: { compact?: boolean }) => (
  <span className={compact ? 'brand-mark brand-mark-compact' : 'brand-mark'}>
    <span className="brand-index">LH</span>
    <span className="brand-name">LOOP<br />HAMBURG</span>
    {!compact && <span className="brand-caption">MANAGEMENT<br />DASHBOARD</span>}
  </span>
);

const NavGroup = ({ id, label, open, onToggle, instanceId, children }: {
  id: MenuGroupId; label: string; open: boolean; onToggle: (id: MenuGroupId) => void;
  instanceId: 'desktop' | 'mobile'; children: ReactNode;
}) => (
  <div className="sidebar-group">
    <button type="button" className="sidebar-group-toggle" aria-expanded={open}
      aria-controls={`${instanceId}-group-${id}`} onClick={() => onToggle(id)}>{label}<span aria-hidden="true">⌄</span></button>
    <div id={`${instanceId}-group-${id}`} hidden={!open}>{children}</div>
  </div>
);

interface SidebarLinkProps {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

const SidebarLink = ({ to, label, icon, end }: SidebarLinkProps) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => `sidebar-link${isActive ? ' sidebar-link-active' : ''}`}
  >
    <MenuIcon name={icon} />
    <span>{label}</span>
  </NavLink>
);

const MenuIcon = ({ name }: { name: IconName }) => {
  const paths: Record<IconName, ReactNode> = {
    calendar: <><rect x="3" y="5" width="18" height="16" rx="1" /><path d="M7 3v4M17 3v4M3 10h18M8 14h2M14 14h2" /></>,
    overview: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>,
    sales: <><path d="M4 19V9" /><path d="M10 19V5" /><path d="M16 19v-7" /><path d="M22 19V3" /></>,
    food: <><path d="M7 3v8" /><path d="M4 3v5a3 3 0 0 0 6 0V3" /><path d="M7 11v10" /><path d="M17 3c-2 2-3 5-3 8h4v10" /></>,
    labor: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-4 2-7 6-7s6 3 6 7" /><path d="M16 8h5M18.5 5.5v5" /></>,
    cost: <><path d="M4 7h16v13H4z" /><path d="M8 7V4h8v3" /><path d="M8 12h8M8 16h5" /></>,
    loan: <><path d="M3 9 12 4l9 5" /><path d="M5 10v8M9 10v8M15 10v8M19 10v8" /><path d="M3 20h18" /></>,
    upload: <><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M4 15v5h16v-5" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><path d="m15 9 6-6" /></>,
    store: <><path d="M4 10v10h16V10" /><path d="M3 10 5 4h14l2 6" /><path d="M8 20v-6h8v6" /></>,
    integration: <><path d="M7 7h10v10H7z" /><path d="M3 12h4M17 12h4M12 3v4M12 17v4" /><path d="m9.5 12 1.5 1.5 3.5-4" /></>,
    trend: <><path d="M3 18 9 12l4 3 8-9" /><path d="M16 6h5v5" /><path d="M3 21h18" /></>,
  };
  return <svg className="sidebar-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
};
